/**
 * The rendered look: sky, sun, soft shadows and ambient occlusion.
 *
 * The diorama reference this world is styled after is a set of offline renders,
 * and nearly everything that makes them read as "that style" is light, not
 * geometry: a warm sun with soft contact shadows under every object, occlusion
 * pooling where a wall meets the ground, saturated grass, and a pale sky that
 * the far distance dissolves into. This module owns all of that, so the rest of
 * the renderer can stay about geometry.
 *
 * Two things keep it affordable in a streaming world:
 *
 * - **The shadow frustum follows the player.** One directional light covers a
 *   box of a couple of hundred metres around the viewer and is re-centred every
 *   frame, snapped to whole shadow texels so edges do not crawl as the player
 *   walks. Distant terrain is unshadowed, which is invisible from the ground
 *   and irrelevant from the air.
 * - **Occlusion reads the scene's own depth.** GTAO would otherwise draw the
 *   whole scene a second time just for normals and depth — more triangles than
 *   the scene pass itself. Everything here is flat-shaded, so normals rebuilt
 *   from the depth buffer are the faceted normals anyway.
 * - **Occlusion is switched off from altitude.** Above a few hundred metres
 *   nothing it adds is resolvable, so a parachute drop does not pay for it.
 * - **The night grade only runs at night.** A full-screen pass that does
 *   nothing in daylight is skipped until the sun goes down.
 */

import type { PerspectiveCamera, Scene, WebGLRenderer } from 'three';
import {
  AdditiveBlending,
  BackSide,
  BufferAttribute,
  BufferGeometry,
  CircleGeometry,
  Color,
  DepthTexture,
  IcosahedronGeometry,
  Points,
  ShaderMaterial,
  DirectionalLight,
  Fog,
  HalfFloatType,
  HemisphereLight,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  NeutralToneMapping,
  PCFShadowMap,
  SphereGeometry,
  Vector2,
  Vector3,
  WebGLRenderTarget,
} from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { FXAAPass } from 'three/addons/postprocessing/FXAAPass.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';

/**
 * Night vision. In the dark the eye's rods take over from its cones: colour
 * drains away and what is left shifts toward blue — the Purkinje shift. This
 * grades the linear image toward a blue-tinted luminance as the night deepens,
 * which is why a moonlit lawn reads as silver-blue instead of murky green, and
 * why lit windows, which stay bright enough for the cones, keep their warmth.
 */
const NightShader = {
  uniforms: { tDiffuse: { value: null }, uNight: { value: 0 } },
  vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: `uniform sampler2D tDiffuse; uniform float uNight; varying vec2 vUv;
    void main() {
      vec4 c = texture2D(tDiffuse, vUv);
      float lum = dot(c.rgb, vec3(0.2126, 0.7152, 0.0722));
      // Warm artificial lights (amber/gold where red exceeds both green and blue) trigger cones even at soft falloff.
      float warmth = clamp((c.r - max(c.b * 0.85, c.g * 0.94)) * 5.0, 0.0, 1.0);
      // Bright things and warm lights stay in colour: rods only grade cool, dim moonlit surfaces.
      float rods = uNight * (1.0 - smoothstep(0.03, 0.24, lum)) * (1.0 - warmth);
      vec3 scotopic = pow(max(lum, 0.0), 0.86) * 1.15 * vec3(0.68, 0.84, 1.22);
      gl_FragColor = vec4(mix(c.rgb, scotopic, rods * 0.72), c.a);
    }`,
};
import type { Biome, BiomeWeights } from '../core/biome/biome.js';
import { BIOMES } from '../core/biome/biome.js';
import { makeRng } from '../core/build/rng.js';

/**
 * Where the sky's bodies are and what the weather is doing, in scene terms.
 * Built each frame by the caller from `core/sky/astronomy.ts` and the weather.
 */
export interface SkyInput {
  /** Unit direction toward the sun, scene frame (X east, Y up, Z south). */
  sun: Vector3;
  sunAltitude: number;
  moon: Vector3;
  moonAltitude: number;
  /** Lit fraction of the moon's disc, 0..1. */
  moonIllumination: number;
  /** Apparent diameter, radians. */
  moonDiameter: number;
  /** Local sidereal time, radians, and latitude, degrees — for the stars. */
  sidereal: number;
  latitude: number;
  /** 0 clear .. 1 overcast. */
  cloud: number;
  /** 0 clear .. 1 thick fog. */
  fog: number;
  /** 0..1, a lightning flash this frame. */
  flash: number;
}

/**
 * Sky colours through the day, keyed by sun altitude. Daytime colours come
 * from the biome (see ATMOSPHERE); these are the golden hour, sunset, the blue
 * hour and night, which are the same everywhere.
 */
const SUNSET = { horizon: new Color(0xffa25e), zenith: new Color(0x5a7cc0) };
const TWILIGHT = { horizon: new Color(0x7a6488), zenith: new Color(0x1e2a52) };
const NIGHT = { horizon: new Color(0x172038), zenith: new Color(0x070b1a) };
const OVERCAST = new Color(0xb4b9c1);
const GLOW = new Color(0xffb070);
const FLASH_HORIZON = new Color(0xdfe6ff);
const FLASH_ZENITH = new Color(0xc8d2ff);
const GOLDEN_SUN = new Color(0xff9652);
const DAY_SKY_FILL = new Color(0xdcecff);
const GOLDEN_SKY_FILL = new Color(0xf7d2b4);
const TWILIGHT_SKY_FILL = new Color(0x687cb8);
const NIGHT_SKY_FILL = new Color(0x4a64a8);
const GOLDEN_GROUND_FILL = new Color(0xd49b62);
const NIGHT_GROUND_FILL = new Color(0x28344e);
const SUN_DISC_DAY = new Color(0xfff4dc);
const SUN_DISC_GOLDEN = new Color(0xff8a3c);
const ORIGIN = new Vector3(0, 0, 0);
const UP_Y = new Vector3(0, 1, 0);
const UP_Z = new Vector3(0, 0, 1);

function smooth(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/** Radians, for readability of the altitude thresholds. */
const D2R = Math.PI / 180;

/**
 * The sky and light of each biome: dusty warm haze over a desert, humid
 * brightness over a rainforest, cold clear air over ice. Blended by weight
 * like everything else, and eased toward over several seconds, so flying from
 * Sweden to the Sahara the sky warms as the ground turns to sand.
 */
interface Atmosphere {
  horizon: number;
  zenith: number;
  sun: number;
  sunIntensity: number;
  /** Bounce light from the ground — green over grass, gold over sand. */
  bounce: number;
  water: number;
  /** Where the haze starts, metres. Deserts and jungles are hazier. */
  fogNear: number;
}

const ATMOSPHERE: Record<Biome, Atmosphere> = {
  temperate: { horizon: 0xf1e3cf, zenith: 0x8fc3ea, sun: 0xfff0d8, sunIntensity: 3.1, bounce: 0xa3ad7a, water: 0x43b9d1, fogNear: 2500 },
  boreal: { horizon: 0xebe6da, zenith: 0x88b9e0, sun: 0xfff0dc, sunIntensity: 2.9, bounce: 0x8e9f66, water: 0x3a8fb0, fogNear: 2800 },
  tundra: { horizon: 0xe6ebee, zenith: 0x8fb7da, sun: 0xfff4e6, sunIntensity: 2.8, bounce: 0xb5b89a, water: 0x3f82a6, fogNear: 3000 },
  glacier: { horizon: 0xe9eff6, zenith: 0x7eb0e2, sun: 0xfff8f0, sunIntensity: 2.9, bounce: 0xe6ecf2, water: 0x3f7fa6, fogNear: 3200 },
  mediterranean: { horizon: 0xf5e6cc, zenith: 0x7fbbe8, sun: 0xfff2d8, sunIntensity: 3.4, bounce: 0xc7bf86, water: 0x2fb3d0, fogNear: 2600 },
  steppe: { horizon: 0xefe0c4, zenith: 0x92c0e2, sun: 0xfff0d6, sunIntensity: 3.2, bounce: 0xc2b47e, water: 0x4aa8b8, fogNear: 2200 },
  desert: { horizon: 0xf2dcb8, zenith: 0x9cc3e0, sun: 0xfff1d6, sunIntensity: 3.5, bounce: 0xd8c08c, water: 0x3fb6b8, fogNear: 1700 },
  savanna: { horizon: 0xf3dfc0, zenith: 0x8ec0e6, sun: 0xffeccc, sunIntensity: 3.3, bounce: 0xc6b474, water: 0x3aa9b0, fogNear: 2000 },
  rainforest: { horizon: 0xe3eedc, zenith: 0x7fb6e0, sun: 0xfff3de, sunIntensity: 2.9, bounce: 0x5f9a4a, water: 0x3aa38e, fogNear: 1500 },
};

function blendAtmosphere(weights: BiomeWeights): Atmosphere {
  const channels = (key: 'horizon' | 'zenith' | 'sun' | 'bounce' | 'water') => {
    let r = 0, g = 0, b = 0;
    for (const biome of BIOMES) {
      const w = weights[biome];
      const hex = ATMOSPHERE[biome][key];
      r += ((hex >> 16) & 255) * w; g += ((hex >> 8) & 255) * w; b += (hex & 255) * w;
    }
    return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b);
  };
  let sunIntensity = 0;
  let fogNear = 0;
  for (const biome of BIOMES) {
    sunIntensity += ATMOSPHERE[biome].sunIntensity * weights[biome];
    fogNear += ATMOSPHERE[biome].fogNear * weights[biome];
  }
  return {
    horizon: channels('horizon'), zenith: channels('zenith'), sun: channels('sun'),
    bounce: channels('bounce'), water: channels('water'), sunIntensity, fogNear,
  };
}

export interface LookOptions {
  shadows: boolean;
  ambientOcclusion: boolean;
  /** Shadow map resolution. 2048 covers the frustum at ~10 cm a texel. */
  shadowMapSize: number;
  /** Half-width of the shadowed box around the player, metres. */
  shadowExtent: number;
  /** MSAA samples on the scene's target: 4, or 0 on a phone, whose dense screen hardly needs it. */
  msaa?: number;
  /** The occlusion's resolution, a fraction of the frame's. */
  aoScale?: number;
  /** FXAA over the final image. */
  fxaa?: boolean;
}

export const DEFAULT_LOOK: LookOptions = {
  shadows: true,
  ambientOcclusion: true,
  shadowMapSize: 2048,
  shadowExtent: 110,
};

/** Sky at the horizon — also the fog colour, so the world fades into it. */
export const HORIZON = 0xf1e3cf;
const ZENITH = 0x8fc3ea;
/** Toward the sun: where the light comes from, not where it points. */
const SUN_DIRECTION = new Vector3(-0.5, 0.78, -0.38).normalize();
/** Above this height the occlusion pass is not worth its cost. */
const AO_CEILING = 320;
/** How far up the sun direction the shadow camera sits above the focus. */
const SUN_DISTANCE = 400;

/**
 * A gradient sky dome shaded per pixel: Rayleigh zenith-to-horizon falloff,
 * Mie solar aureole and golden-hour horizon glow, opposite-horizon Belt of
 * Venus at twilight, and a soft lunar halo at night. Unlit, unfogged, always
 * drawn first and never written to depth, so it costs one cheap draw with
 * zero per-frame vertex buffer uploads.
 */
function createSky(radius: number): { mesh: Mesh; material: ShaderMaterial } {
  const geometry = new SphereGeometry(radius, 32, 16);
  const material = new ShaderMaterial({
    uniforms: {
      uHorizon: { value: new Color(HORIZON) },
      uZenith: { value: new Color(ZENITH) },
      uSun: { value: SUN_DIRECTION.clone() },
      uMoon: { value: new Vector3(0, -1, 0) },
      uGlowColor: { value: GLOW.clone() },
      uGlow: { value: 0 },
      uCorona: { value: 1 },
      uGolden: { value: 0 },
      uTwilight: { value: 0 },
      uMoonGlow: { value: 0 },
    },
    vertexShader: `
      uniform vec3 uHorizon;
      uniform vec3 uZenith;
      uniform vec3 uSun;
      uniform vec3 uGlowColor;
      uniform float uGlow;
      uniform float uTwilight;
      varying vec3 vDir;
      varying vec3 vSkyBase;
      void main() {
        vDir = position;
        vec3 dir = normalize(position);
        float h = dir.y;
        vec3 c;
        if (h >= 0.0) {
          c = mix(uHorizon, uZenith, pow(h, 0.52));
          // Soft atmospheric horizon mist so distant fogged terrain melts seamlessly into the sky.
          float mist = exp(-h * 14.0) * 0.18;
          c = mix(c, uHorizon, mist);
        } else {
          c = mix(uHorizon, uHorizon * 0.92, min(1.0, -h * 4.0));
        }

        // Anti-solar twilight arch: Earth's dark slate shadow band and rose-pink Belt of Venus opposite a low sun.
        if (uTwilight > 0.001 && h > -0.02) {
          vec2 horizDir = normalize(vec2(dir.x, dir.z) + vec2(1e-5, 0.0));
          vec2 sunHoriz = normalize(vec2(uSun.x, uSun.z) + vec2(1e-5, 0.0));
          float anti = max(0.0, -dot(horizDir, sunHoriz));
          float venusBand = smoothstep(0.01, 0.11, h) * (1.0 - smoothstep(0.11, 0.30, h));
          float earthShadow = smoothstep(-0.02, 0.02, h) * (1.0 - smoothstep(0.02, 0.09, h));
          c = mix(c, vec3(0.14, 0.19, 0.34), earthShadow * (0.35 + 0.45 * anti) * uTwilight);
          c += vec3(0.32, 0.12, 0.22) * venusBand * (0.35 + 0.65 * anti * anti) * uTwilight;
        }

        // Golden-hour horizon glow under and around a low sun.
        if (uGlow > 0.001) {
          float sunDot = max(0.0, dot(dir, uSun));
          float s2 = sunDot * sunDot;
          float g = (s2 * s2 * sunDot) * uGlow * (1.0 - min(1.0, abs(h) * 1.55));
          c += uGlowColor * g;
        }
        vSkyBase = c;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: `
      uniform vec3 uSun;
      uniform vec3 uMoon;
      uniform float uCorona;
      uniform float uGolden;
      uniform float uMoonGlow;
      varying vec3 vDir;
      varying vec3 vSkyBase;
      void main() {
        vec3 dir = normalize(vDir);
        float h = dir.y;
        vec3 c = vSkyBase;

        // Mie forward-scattering solar aureole around the sun disc (fast squaring instead of pow).
        if (uCorona > 0.001 && h > -0.08) {
          float sunDot = max(0.0, dot(dir, uSun));
          float s2 = sunDot * sunDot;
          float s4 = s2 * s2;
          float s8 = s4 * s4;
          float s24 = s8 * s8 * s8;
          float s96 = s24 * s24 * s24 * s24;
          float horizonFade = smoothstep(-0.08, 0.02, h);
          vec3 coronaTint = mix(vec3(1.0, 0.95, 0.84), vec3(1.0, 0.58, 0.28), uGolden);
          c += coronaTint * ((s24 * 0.14 + s96 * s24 * s24 * 0.42) * uCorona * horizonFade);
        }

        // Silver-blue lunar corona around the moon at night.
        if (uMoonGlow > 0.001 && h > -0.05) {
          float moonDot = max(0.0, dot(dir, uMoon));
          float m2 = moonDot * moonDot;
          float m4 = m2 * m2;
          float m16 = m4 * m4;
          float m96 = m16 * m16 * m16 * m16 * m16 * m16;
          float lunarHalo = (m16 * m2 * 0.12 + m96 * m16 * 0.28) * uMoonGlow * smoothstep(-0.05, 0.04, h);
          c += vec3(0.58, 0.72, 0.98) * lunarHalo;
        }

        gl_FragColor = vec4(c, 1.0);
      }`,
    side: BackSide,
    fog: false,
    depthWrite: false,
  });
  const mesh = new Mesh(geometry, material);
  mesh.name = 'sky';
  mesh.renderOrder = -1000;
  mesh.frustumCulled = false;
  return { mesh, material };
}

/**
 * A fixed star field in the equatorial frame: a few thousand stars with a
 * realistic brightness and colour-temperature distribution (many faint, few
 * bright; warm amber K/M giants to crisp blue-white O/B stars), plus Polaris
 * at its true position so the sky visibly turns about it.
 */
function createStars(radius: number): BufferGeometry {
  const rng = makeRng(0x57a25);
  const count = 2600;
  const positions = new Float32Array((count + 1) * 3);
  const sizes = new Float32Array(count + 1);
  const bright = new Float32Array(count + 1);
  const temp = new Float32Array(count + 1);
  for (let i = 0; i < count; i++) {
    const zc = rng() * 2 - 1;
    const t = rng() * Math.PI * 2;
    const r = Math.sqrt(1 - zc * zc);
    positions.set([r * Math.cos(t) * radius, r * Math.sin(t) * radius, zc * radius], i * 3);
    // Magnitude-like: brightness falls off as a power of a uniform variate.
    const m = Math.pow(rng(), 3.2);
    sizes[i] = 1.2 + m * 3.2;
    bright[i] = 0.25 + m * 0.75;
    temp[i] = rng();
  }
  // Polaris: RA 2h 31m 49s, Dec +89° 15' (F-type supergiant, warm cream-white).
  const ra = 37.95 * D2R, dec = 89.264 * D2R;
  positions.set([Math.cos(dec) * Math.cos(ra) * radius, Math.cos(dec) * Math.sin(ra) * radius, Math.sin(dec) * radius], count * 3);
  sizes[count] = 3.6;
  bright[count] = 1;
  temp[count] = 0.42;
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(positions, 3));
  g.setAttribute('aSize', new BufferAttribute(sizes, 1));
  g.setAttribute('aBright', new BufferAttribute(bright, 1));
  g.setAttribute('aTemp', new BufferAttribute(temp, 1));
  return g;
}

export class Look {
  readonly sun: DirectionalLight;
  /** The water colour of the current biome, for the water material. */
  readonly waterColor = new Color(0x43b9d1);
  private readonly hemisphere: HemisphereLight;
  private target: Atmosphere = ATMOSPHERE.temperate;
  private climateKnown = false;
  private readonly current = {
    horizon: new Color(HORIZON), zenith: new Color(ZENITH), sun: new Color(0xfff0d8),
    bounce: new Color(0xa3ad7a), water: new Color(0x43b9d1), sunIntensity: 3.1, fogNear: 2500,
  };
  private readonly goal = {
    horizon: new Color(), zenith: new Color(), sun: new Color(), bounce: new Color(), water: new Color(),
  };
  private composer!: EffectComposer;
  private gtao: GTAOPass | undefined;
  /** Above the occlusion ceiling: kept so a rebuilt pass starts as the old one left off. */
  private aoWanted = true;
  private readonly nightPass: ShaderPass;
  private readonly sky: Mesh;
  private readonly skyMaterial: ShaderMaterial;
  /** Light frame to world, and back — rebuilt as the key light moves. */
  private readonly lightToWorld = new Matrix4();
  private readonly worldToLight = new Matrix4();
  private readonly scratch = new Vector3();
  /** Direction toward whichever of sun or moon is lighting the scene. */
  private readonly keyDirection = SUN_DIRECTION.clone();
  private skyInput: SkyInput | undefined;
  private readonly sunDisc: Mesh;
  private readonly moon: Mesh;
  private readonly moonMaterial: ShaderMaterial;
  private readonly stars: Points;
  private readonly starMaterial: ShaderMaterial;
  private readonly starMatrix = new Matrix4();
  private readonly starRotation = new Matrix4();
  private readonly starFrame = new Matrix4();
  private starClock = 0;
  /** 0 full day .. 1 full night, for the night-time materials. */
  night = 0;
  /** Share of windows lit, from the local hour; set by the caller. */
  windowsLit = 0;

  constructor(
    private readonly renderer: WebGLRenderer,
    private readonly scene: Scene,
    private readonly camera: PerspectiveCamera,
    private options: LookOptions = DEFAULT_LOOK,
  ) {
    // Neutral rather than ACES: ACES shifts saturated greens toward yellow and
    // flattens pastels, and the reference palette is exactly saturated greens
    // and pastels. Khronos Neutral keeps hue and chroma through the highlights.
    renderer.toneMapping = NeutralToneMapping;
    renderer.toneMappingExposure = 1.0;

    scene.background = new Color(HORIZON);
    scene.fog = new Fog(HORIZON, 2_500, 42_000);
    const skyDome = createSky(camera.far * 0.9);
    this.sky = skyDome.mesh;
    this.skyMaterial = skyDome.material;
    scene.add(this.sky);

    this.sunDisc = new Mesh(new CircleGeometry(1, 14), new MeshBasicMaterial({
      color: 0xfff4dc, fog: false, transparent: true, depthWrite: false,
    }));
    this.sunDisc.renderOrder = -900;
    this.sunDisc.frustumCulled = false;
    scene.add(this.sunDisc);

    // The moon: a faceted sphere lit by the true sun direction, so its phase
    // and the tilt of its terminator come from the geometry, not a texture.
    this.moonMaterial = new ShaderMaterial({
      uniforms: { uSun: { value: new Vector3(0, 1, 0) }, uDay: { value: 0 } },
      vertexShader: `varying vec3 vWorld; varying vec3 vLocal;
        void main() {
          vLocal = normalize(position);
          vec4 w = modelMatrix * vec4(position, 1.0);
          vWorld = w.xyz;
          gl_Position = projectionMatrix * viewMatrix * w;
        }`,
      fragmentShader: `uniform vec3 uSun; uniform float uDay; varying vec3 vWorld; varying vec3 vLocal;
        void main() {
          vec3 n = normalize(cross(dFdx(vWorld), dFdy(vWorld)));
          float ndl = dot(n, uSun);
          float lit = smoothstep(-0.04, 0.08, ndl) * (0.35 + 0.65 * max(ndl, 0.0));
          // Subtle lunar maria shading across the disc, plus cool earthshine on the dark side.
          float mare = sin(vLocal.x * 3.1 + 0.6) * cos(vLocal.y * 2.7 - 0.4) + sin(vLocal.z * 4.2) * 0.5;
          float albedo = mix(0.84, 1.0, smoothstep(-0.35, 0.45, mare));
          vec3 sunlit = vec3(0.95, 0.93, 0.87) * albedo * lit * 1.28;
          vec3 earthshine = vec3(0.56, 0.68, 0.92) * albedo * 0.042 * (1.0 - uDay);
          vec3 c = sunlit + earthshine;
          gl_FragColor = vec4(c, mix(1.0, 0.45, uDay) * max(lit, 0.08 * (1.0 - uDay)));
        }`,
      transparent: true,
      depthWrite: false,
      fog: false,
    });
    this.moon = new Mesh(new IcosahedronGeometry(1, 1), this.moonMaterial);
    this.moon.renderOrder = -890;
    this.moon.frustumCulled = false;
    scene.add(this.moon);

    const starRadius = camera.far * 0.8;
    this.starMaterial = new ShaderMaterial({
      uniforms: { uVisible: { value: 0 }, uTime: { value: 0 }, uInvRadius: { value: 1 / starRadius } },
      vertexShader: `attribute float aSize; attribute float aBright; attribute float aTemp;
        uniform float uTime; uniform float uInvRadius;
        varying float vBright; varying float vTemp;
        void main() {
          vTemp = aTemp;
          vec4 w = modelMatrix * vec4(position, 1.0);
          float elev = clamp((w.y - cameraPosition.y) * uInvRadius, -1.0, 1.0);
          float horizonFade = smoothstep(0.005, 0.11, elev);
          // Subtle atmospheric scintillation, stronger near the horizon where airmass is thick.
          float twinkle = 1.0 + (1.0 - smoothstep(0.05, 0.55, elev)) * 0.24 * sin(uTime * (2.2 + aTemp * 3.1) + aBright * 47.0);
          vBright = aBright * horizonFade * twinkle;
          gl_PointSize = aSize;
          gl_Position = projectionMatrix * viewMatrix * w;
        }`,
      fragmentShader: `uniform float uVisible; varying float vBright; varying float vTemp;
        void main() {
          vec2 d = gl_PointCoord - 0.5;
          float a = smoothstep(0.5, 0.1, length(d)) * vBright * uVisible;
          // Planckian-like star tint: warm amber-white K/M stars to crisp blue-white O/B stars.
          vec3 warm = vec3(1.0, 0.86, 0.72);
          vec3 mid = vec3(0.98, 0.98, 1.0);
          vec3 cool = vec3(0.82, 0.90, 1.0);
          vec3 starCol = vTemp < 0.5 ? mix(warm, mid, vTemp * 2.0) : mix(mid, cool, (vTemp - 0.5) * 2.0);
          gl_FragColor = vec4(starCol * a, a);
        }`,
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      fog: false,
    });
    this.stars = new Points(createStars(starRadius), this.starMaterial);
    this.stars.renderOrder = -950;
    this.stars.frustumCulled = false;
    this.stars.matrixAutoUpdate = false;
    scene.add(this.stars);

    this.sun = new DirectionalLight(0xfff0d8, 3.1);
    this.sun.position.copy(SUN_DIRECTION);
    scene.add(this.sun);
    scene.add(this.sun.target);
    // Sky fill from above, bounce from the grass below. The green ground tint
    // is what makes shaded walls read as sitting on a lawn, as in the renders.
    // Strong enough that a white wall with its back to the sun still reads
    // white, as it does in the reference renders; the shadows keep their shape
    // because the sun still carries two thirds of the light.
    this.hemisphere = new HemisphereLight(0xdcecff, 0xa3ad7a, 1.7);
    scene.add(this.hemisphere);

    this.applyShadows();
    this.nightPass = new ShaderPass(NightShader);
    this.buildPost();
  }

  /**
   * Change the look's options while running. Shadows change in place; the
   * occlusion, MSAA and FXAA rebuild the post chain, which is a few render
   * targets, not the scene.
   */
  setOptions(next: LookOptions): void {
    const prev = this.options;
    this.options = next;
    if (next.shadows !== prev.shadows || next.shadowMapSize !== prev.shadowMapSize || next.shadowExtent !== prev.shadowExtent) {
      this.applyShadows();
    }
    if (next.ambientOcclusion !== prev.ambientOcclusion || (next.aoScale ?? 1) !== (prev.aoScale ?? 1)
      || (next.msaa ?? 4) !== (prev.msaa ?? 4) || (next.fxaa ?? false) !== (prev.fxaa ?? false)) {
      this.composer.dispose();
      this.gtao?.dispose();
      this.buildPost();
    }
  }

  private applyShadows(): void {
    const { renderer, options } = this;
    const shadow = this.sun.shadow;
    // Turning them off is the light's: each material's program follows the
    // lights' shadow count, so it recompiles without being told.
    renderer.shadowMap.enabled = options.shadows;
    this.sun.castShadow = options.shadows;
    if (!options.shadows) return;
    renderer.shadowMap.type = PCFShadowMap;
    if (shadow.mapSize.x !== options.shadowMapSize) {
      shadow.mapSize.set(options.shadowMapSize, options.shadowMapSize);
      // Made again at the new size on the next shadow pass.
      shadow.map?.dispose();
      shadow.map = null;
    }
    shadow.radius = 3;
    // Depth bias is a fraction of the shadow camera's range, so it is kept
    // small against a tight range: 1e-4 of 800 m is 8 cm. Anything larger
    // lifts every shadow off the foot of the object casting it.
    shadow.bias = -0.0001;
    shadow.normalBias = 0.045;
    const e = options.shadowExtent;
    Object.assign(shadow.camera, { left: -e, right: e, top: e, bottom: -e, near: 1, far: SUN_DISTANCE * 2 });
    shadow.camera.updateProjectionMatrix();
  }

  private buildPost(): void {
    const { renderer, scene, camera, options } = this;
    const size = renderer.getDrawingBufferSize(new Vector2());
    // The scene pass keeps its depth as a texture — resolved out of the MSAA
    // buffer — so the occlusion can read it instead of redrawing the scene.
    // The composer clones this target for its ping-pong pair, depth included.
    const target = new WebGLRenderTarget(size.x, size.y, {
      type: HalfFloatType,
      samples: options.msaa ?? 4,
      // Only when there is one: three's setter does not take undefined.
      ...(options.ambientOcclusion ? { depthTexture: new DepthTexture(size.x, size.y) } : {}),
    });
    this.composer = new EffectComposer(renderer, target);
    this.composer.addPass(new RenderPass(scene, camera));
    this.gtao = undefined;
    if (options.ambientOcclusion) {
      // At half resolution the occlusion shades a quarter of the pixels and
      // is stretched back over the frame by the blend; contact shadows are
      // soft enough that the stretch does not show. The composer sizes every
      // pass to the frame, so the pass is sized down on its way in.
      const aoScale = options.aoScale ?? 1;
      const scaled = (n: number) => Math.max(1, Math.round(n * aoScale));
      const gtao = new GTAOPass(scene, camera, scaled(size.x), scaled(size.y));
      const setSize = gtao.setSize.bind(gtao);
      gtao.setSize = (width: number, height: number) => setSize(scaled(width), scaled(height));
      // Depth only, no normal texture: the shaders rebuild normals from depth.
      // Everything in this world is flat-shaded — and the houses carry no
      // normal attribute at all — so faceted normals are exactly right, and
      // this saves GTAO drawing the whole scene again to get them.
      gtao.setGBuffer(target.depthTexture!);
      // A tight radius: this is contact occlusion — where a wall meets the lawn,
      // under eaves, between a window and its frame — not a haze over the town.
      gtao.updateGtaoMaterial({ radius: 1.3, distanceExponent: 1.4, thickness: 1.5, scale: 1.1, samples: 8 });
      gtao.updatePdMaterial({ lumaPhi: 10, depthPhi: 2, normalPhi: 3, radius: 5, rings: 1, samples: 8 });
      gtao.blendIntensity = 0.8;
      gtao.enabled = this.aoWanted;
      this.composer.addPass(gtao);
      this.gtao = gtao;
    }
    this.composer.addPass(this.nightPass);
    this.composer.addPass(new OutputPass());
    // After the output pass: FXAA wants the tone-mapped, sRGB image.
    if (options.fxaa === true) {
      const fxaa = new FXAAPass();
      fxaa.setSize(size.x, size.y);
      this.composer.addPass(fxaa);
    }
    this.composer.setPixelRatio(renderer.getPixelRatio());
    this.composer.setSize(size.x / renderer.getPixelRatio(), size.y / renderer.getPixelRatio());
  }

  /** Where the sun and moon are, and the weather, for this frame. */
  setSky(input: SkyInput): void {
    this.skyInput = input;
  }

  /** Aim the sky and light at a biome mix. Eased toward over several seconds. */
  setClimate(weights: BiomeWeights): void {
    this.target = blendAtmosphere(weights);
    // The first climate is applied outright: a player spawning in the Sahara
    // should not watch a Swedish sky fade to desert haze.
    if (!this.climateKnown) {
      this.climateKnown = true;
      this.breathe(1e6);
    }
  }

  /** Ease the atmosphere a step toward its target, and apply it. */
  private breathe(dt: number): void {
    const k = Math.min(1, dt * 0.6);
    const t = this.target;
    const c = this.current;
    const g = this.goal;
    c.horizon.lerp(g.horizon.set(t.horizon), k);
    c.zenith.lerp(g.zenith.set(t.zenith), k);
    c.sun.lerp(g.sun.set(t.sun), k);
    c.bounce.lerp(g.bounce.set(t.bounce), k);
    c.water.lerp(g.water.set(t.water), k);
    c.sunIntensity += (t.sunIntensity - c.sunIntensity) * k;
    c.fogNear += (t.fogNear - c.fogNear) * k;

    this.waterColor.copy(c.water);
    this.applySky();
  }

  private readonly horizonNow = new Color();
  private readonly zenithNow = new Color();
  private readonly tint = new Color();

  /**
   * Light and colour the world for where the sun and moon are.
   *
   * The key light — the one that casts shadows — is the sun while it is up and
   * the moon after dark, whichever is brighter, so moonlit nights still have
   * shadows. Sky colours run from the biome's day colours through golden hour
   * and sunset to the blue hour and night by the sun's altitude, and cloud
   * greys it all down. At night the exposure rises a little, as eyes adjust,
   * which is what keeps a moonlit village readable instead of black.
   */
  private applySky(): void {
    const c = this.current;
    const sky = this.skyInput;
    const alt = sky?.sunAltitude ?? 0.8;
    const cloud = sky?.cloud ?? 0;
    const fogAmount = sky?.fog ?? 0;

    const day = smooth(-6 * D2R, 8 * D2R, alt);
    const golden = (1 - smooth(1 * D2R, 16 * D2R, alt)) * smooth(-5 * D2R, 0, alt);
    const night = 1 - smooth(-14 * D2R, -3 * D2R, alt);
    this.night = night;
    if (this.nightPass !== undefined) this.nightPass.uniforms.uNight.value = night;

    // Sky gradient by sun altitude.
    const h = this.horizonNow.copy(NIGHT.horizon);
    const z = this.zenithNow.copy(NIGHT.zenith);
    const twilight = smooth(-14 * D2R, -4 * D2R, alt);
    h.lerp(TWILIGHT.horizon, twilight); z.lerp(TWILIGHT.zenith, twilight);
    const sunset = smooth(-4 * D2R, 1 * D2R, alt);
    h.lerp(SUNSET.horizon, sunset); z.lerp(SUNSET.zenith, sunset);
    const full = smooth(1 * D2R, 14 * D2R, alt);
    h.lerp(c.horizon, full); z.lerp(c.zenith, full);
    // Cloud and fog: toward a flat grey, lit as the day is.
    const grey = this.tint.copy(OVERCAST).multiplyScalar(0.15 + 0.85 * day);
    h.lerp(grey, Math.max(cloud * 0.75, fogAmount));
    z.lerp(grey, Math.max(cloud * 0.85, fogAmount));
    if (sky !== undefined && sky.flash > 0) {
      h.lerp(FLASH_HORIZON, sky.flash);
      z.lerp(FLASH_ZENITH, sky.flash);
    }

    const clearAir = (1 - cloud * 0.9) * (1 - fogAmount * 0.85);
    const twilightArch = smooth(-9 * D2R, -1 * D2R, alt) * (1 - smooth(0, 6 * D2R, alt)) * clearAir;
    const moonUp = sky === undefined ? 0 : smooth(-1 * D2R, 12 * D2R, sky.moonAltitude);
    const u = this.skyMaterial.uniforms;
    (u.uHorizon.value as Color).copy(h);
    (u.uZenith.value as Color).copy(z);
    (u.uSun.value as Vector3).copy(sky?.sun ?? SUN_DIRECTION);
    if (sky !== undefined) (u.uMoon.value as Vector3).copy(sky.moon);
    u.uGlow.value = golden * 1.45 * (1 - cloud);
    u.uCorona.value = smooth(-2 * D2R, 10 * D2R, alt) * clearAir;
    u.uGolden.value = golden;
    u.uTwilight.value = twilightArch;
    u.uMoonGlow.value = moonUp * (sky?.moonIllumination ?? 0) * night * clearAir;

    (this.scene.background as Color).copy(h);
    const fog = this.scene.fog as Fog;
    fog.color.copy(h);
    fog.near = c.fogNear * (1 - fogAmount) + 40 * fogAmount;
    fog.far = 42_000 * (1 - fogAmount) + 700 * fogAmount;

    // Key light: sun, or the moon (plus subtle starlight/airglow floor) at night.
    const sunPower = c.sunIntensity * smooth(-2 * D2R, 10 * D2R, alt) * (1 - 0.78 * cloud) * (1 - 0.6 * fogAmount);
    const moonPower = (0.22 + 1.05 * (sky?.moonIllumination ?? 0) * moonUp) * night * (1 - 0.8 * cloud);
    if (sky === undefined || sunPower >= moonPower) {
      this.keyDirection.copy(sky?.sun ?? SUN_DIRECTION);
      if (this.keyDirection.y < 0.02) this.keyDirection.y = 0.02;
      this.sun.color.copy(c.sun).lerp(GOLDEN_SUN, golden * 0.82);
      this.sun.intensity = sunPower;
    } else {
      if (moonUp > 0.05) {
        this.keyDirection.copy(sky.moon);
      } else {
        this.keyDirection.set(0.25, 0.88, 0.4);
      }
      if (this.keyDirection.y < 0.15) this.keyDirection.y = 0.15;
      this.sun.color.set(0xb4cbff);
      this.sun.intensity = moonPower;
    }
    this.keyDirection.normalize();
    if (this.sun.shadow !== undefined) {
      // Soft shadows under cloud: an overcast sky casts none worth drawing.
      this.sun.shadow.intensity = 1 - 0.85 * cloud;
    }

    // Fill: the sky's own light — crisp in daylight, warm rose-amber in golden
    // hour, cobalt in the blue hour, and deep silver-blue at night.
    const blueHour = (1 - day) * (1 - night);
    this.hemisphere.color.copy(DAY_SKY_FILL)
      .lerp(GOLDEN_SKY_FILL, golden * 0.55 * (1 - cloud))
      .lerp(TWILIGHT_SKY_FILL, blueHour * 0.65)
      .lerp(NIGHT_SKY_FILL, night);
    this.hemisphere.groundColor.copy(c.bounce)
      .lerp(GOLDEN_GROUND_FILL, golden * 0.35 * (1 - cloud))
      .lerp(NIGHT_GROUND_FILL, night);
    this.hemisphere.intensity = (1.7 * day + 0.95 * night + 0.45 * blueHour)
      * (1 + 0.15 * cloud) + (sky?.flash ?? 0) * 4;
    this.renderer.toneMappingExposure = 1 + 1.25 * night;

    // Sun disc and moon.
    if (sky !== undefined) {
      const distance = this.camera.far * 0.8;
      this.sunDisc.visible = alt > -2 * D2R && cloud < 0.95;
      this.sunDisc.position.copy(this.camera.position).addScaledVector(sky.sun, distance);
      this.sunDisc.lookAt(this.camera.position);
      this.sunDisc.scale.setScalar(distance * Math.tan(0.53 * D2R) * 1.6);
      (this.sunDisc.material as MeshBasicMaterial).color.copy(SUN_DISC_DAY).lerp(SUN_DISC_GOLDEN, golden);
      (this.sunDisc.material as MeshBasicMaterial).opacity = 1 - cloud;

      this.moon.visible = sky.moonAltitude > -1 * D2R && cloud < 0.95;
      this.moon.position.copy(this.camera.position).addScaledVector(sky.moon, distance * 0.97);
      // Drawn at a little over twice its true size: at its real half degree it
      // is a dozen pixels, too small to read a phase from.
      this.moon.scale.setScalar(distance * Math.tan(sky.moonDiameter / 2) * 2.2);
      (this.moonMaterial.uniforms.uSun.value as Vector3).copy(sky.sun);
      this.moonMaterial.uniforms.uDay.value = day;

      // Stars: equatorial frame into the scene, by sidereal time and latitude.
      const phi = sky.latitude * D2R;
      const rot = this.starRotation.makeRotationZ(-sky.sidereal);
      const toScene = this.starFrame.set(
        0, 1, 0, 0,
        Math.cos(phi), 0, Math.sin(phi), 0,
        Math.sin(phi), 0, -Math.cos(phi), 0,
        0, 0, 0, 1,
      );
      this.starMatrix.multiplyMatrices(toScene, rot);
      this.starMatrix.setPosition(this.camera.position);
      this.stars.matrix.copy(this.starMatrix);
      this.stars.matrixWorldNeedsUpdate = true;
      this.starMaterial.uniforms.uVisible.value = night * (1 - cloud) * (1 - fogAmount);
    }
  }

  /**
   * Re-centre the sun and its shadow on `focus` (scene metres) and decide
   * whether occlusion is worth drawing from `heightAboveGround`.
   */
  update(focus: Vector3, heightAboveGround: number, dt = 1 / 60): void {
    this.starClock += dt;
    this.starMaterial.uniforms.uTime.value = this.starClock;
    this.breathe(dt);
    this.sky.position.copy(this.camera.position);

    if (this.options.shadows) {
      // Snap the focus to the shadow map's texel grid, measured in the light's
      // own frame. Without this every sub-texel step of the player shifts which
      // texels an edge falls in, and every shadow in view shimmers.
      const texel = (this.options.shadowExtent * 2) / this.options.shadowMapSize;
      this.lightToWorld.lookAt(this.keyDirection, ORIGIN, Math.abs(this.keyDirection.y) > 0.99 ? UP_Z : UP_Y);
      this.worldToLight.copy(this.lightToWorld).invert();
      const inLight = this.scratch.copy(focus).applyMatrix4(this.worldToLight);
      inLight.x = Math.round(inLight.x / texel) * texel;
      inLight.y = Math.round(inLight.y / texel) * texel;
      const snapped = inLight.applyMatrix4(this.lightToWorld);
      this.sun.target.position.copy(snapped);
      this.sun.position.copy(snapped).addScaledVector(this.keyDirection, SUN_DISTANCE);
      this.sun.target.updateMatrixWorld();
      this.sun.updateMatrixWorld();
    } else {
      this.sun.position.copy(focus).add(this.keyDirection);
      this.sun.target.position.copy(focus);
    }

    this.aoWanted = heightAboveGround < AO_CEILING;
    if (this.gtao !== undefined) this.gtao.enabled = this.aoWanted;
  }

  render(): void {
    if (this.gtao !== undefined && this.gtao.enabled) {
      // The scene pass draws into whichever of the composer's pair is the read
      // buffer this frame, and the pair swaps an odd number of times per frame,
      // so the depth the occlusion reads has to follow it.
      const depth = this.composer.readBuffer.depthTexture;
      this.gtao.gtaoMaterial.uniforms.tDepth.value = depth;
      this.gtao.pdMaterial.uniforms.tDepth.value = depth;
    }
    this.nightPass.enabled = this.night > 0.001;
    this.composer.render();
  }

  resize(width: number, height: number): void {
    this.composer.setPixelRatio(this.renderer.getPixelRatio());
    this.composer.setSize(width, height);
  }
}
